import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import CartToast from './components/CartToast';
import { trackEvent } from './utils/analytics';
import { apiUrl } from './config';
import { repriceLines } from './utils/cartPricing';

const CartContext = createContext(null);

const STORAGE_KEY = 'browmie_cart';
const TOKEN_KEY = 'browmie_line_token';

// A cart line is uniquely identified by its product id + the specific flavor
// selection (so the same bundle with different flavors are separate lines).
const lineKey = (productId, flavors) =>
  `${productId}::${flavors && flavors.length ? JSON.stringify(flavors) : ''}`;

const loadInitial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitial);
  // Last add-to-cart event — drives the "added to cart" toast. ts makes
  // consecutive adds (even of the same product) re-trigger it.
  const [lastAdded, setLastAdded] = useState(null);
  const [lineCartToken, setLineCartTokenState] = useState(
    () => localStorage.getItem(TOKEN_KEY) || null
  );
  // Customer contact info (name/phone/address) held ONLY in memory — never
  // persisted to localStorage. Set from the bot cart link's bound profile, or
  // fetched from the account at checkout. Lost on a full reload by design.
  const [customerProfile, setCustomerProfile] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items]);

  // Keep saved cart lines on today's prices: once when the site opens and again
  // when the customer comes back to the tab (at most every 5 minutes).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => {
    let lastRun = 0;
    let cancelled = false;
    const refresh = async () => {
      if (!itemsRef.current.length || typeof fetch !== 'function' || Date.now() - lastRun < 5 * 60 * 1000) return;
      lastRun = Date.now();
      try {
        const response = await fetch(apiUrl('/api/system/public/products'));
        const json = await response.json();
        if (cancelled || !json?.success || !Array.isArray(json.data)) return;
        setItems((prev) => repriceLines(prev, json.data));
      } catch { /* keep the saved prices; checkout still recomputes */ }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    refresh();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const setLineCartToken = (token) => {
    setLineCartTokenState(token);
    try {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  };

  // item: { productId, name, price, discountedPrice?, image, type, quantity, flavors?, bundlePieces?, bundleMode? }
  // `price` is always the ORIGINAL unit price; `discountedPrice` (when present
  // and lower) is the per-unit sale price. The summary keeps the subtotal at the
  // original and breaks the saving out as its own line.
  const addItem = (item, { placement = 'website' } = {}) => {
    setLastAdded({ name: item.name, ts: Date.now() });
    trackEvent('add_to_cart', {
      productName: item.name,
      productType: item.type || '',
      quantity: item.quantity || 1,
      quantityDelta: item.quantity || 1,
      price: Number(item.discountedPrice ?? item.price) || 0,
      isBundle: !!item.flavors?.length,
      placement,
    }, { productId: item.productId });
    const key = lineKey(item.productId, item.flavors);
    setItems((prev) => {
      const existing = prev.find((line) => line.key === key);
      if (existing) {
        return prev.map((line) =>
          line.key === key ? { ...line, quantity: line.quantity + (item.quantity || 1) } : line
        );
      }
      return [...prev, { ...item, key, quantity: item.quantity || 1 }];
    });
  };

  const updateQuantity = (key, quantity) => {
    const current = items.find((line) => line.key === key);
    if (current && quantity !== current.quantity) {
      const delta = quantity - current.quantity;
      trackEvent(delta > 0 ? 'add_to_cart' : 'remove_from_cart', {
        productName: current.name,
        productType: current.type || '',
        quantity: Math.max(0, quantity),
        quantityDelta: delta,
        price: Number(current.discountedPrice ?? current.price) || 0,
        isBundle: !!current.flavors?.length,
        placement: 'cart_stepper',
      }, { productId: current.productId });
    }
    setItems((prev) =>
      prev
        .map((line) => (line.key === key ? { ...line, quantity } : line))
        .filter((line) => line.quantity > 0)
    );
  };

  const removeItem = (key) => {
    const current = items.find((line) => line.key === key);
    if (current) {
      trackEvent('remove_from_cart', {
        productName: current.name,
        productType: current.type || '',
        quantity: 0,
        quantityDelta: -current.quantity,
        price: Number(current.discountedPrice ?? current.price) || 0,
        isBundle: !!current.flavors?.length,
        placement: 'remove_button',
      }, { productId: current.productId });
    }
    setItems((prev) => prev.filter((line) => line.key !== key));
  };

  const clearCart = () => {
    setItems([]);
    setLineCartToken(null);
  };

  // Replace the whole cart (used when prefilling from a LINE cart link).
  const replaceCart = (newItems) =>
    setItems(
      newItems.map((item) => ({
        ...item,
        key: lineKey(item.productId, item.flavors),
        quantity: item.quantity || 1,
      }))
    );

  const totalCount = useMemo(
    () => items.reduce((sum, line) => sum + line.quantity, 0),
    [items]
  );
  const subtotal = useMemo(
    () => items.reduce((sum, line) => sum + Number(line.price) * line.quantity, 0),
    [items]
  );
  // Total flat per-product saving across the cart (original − discounted, per
  // unit × qty). Broken out as its own summary line; the subtotal stays original.
  const productDiscount = useMemo(
    () => items.reduce((sum, line) => {
      const orig = Number(line.price) || 0;
      const disc = line.discountedPrice != null ? Number(line.discountedPrice) : orig;
      const off = (orig - disc) * line.quantity;
      return sum + (off > 0 ? off : 0);
    }, 0),
    [items]
  );

  const value = {
    items,
    addItem,
    lastAdded,
    updateQuantity,
    removeItem,
    clearCart,
    replaceCart,
    totalCount,
    subtotal,
    productDiscount,
    lineCartToken,
    setLineCartToken,
    customerProfile,
    setCustomerProfile,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
      <CartToast />
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
